<template>
  <v-card
    class="mx-auto d-flex flex-column"
    min-width="15vw"
    max-width="344"
    v-bind="props"
  >
    <v-card-title class="pb-2"> Damage Waivers </v-card-title>

    <v-divider />

    <v-card-text class="pa-3">
      <v-list density="comfortable">
        <v-list-item
          v-for="product in productOptions"
          :key="product.productListing.assignId"
          class="px-0"
        >
          <div class="d-flex align-center justify-space-between w-100">
            <!-- Left side: name + price -->
            <div>
              <div class="text-subtitle-1 font-weight-medium">
                {{ getName(product) }}
              </div>
              <div class="text-body-2 text-medium-emphasis">
                ${{ getPrice(product) }}
              </div>
            </div>

            <!-- Right side: buttons -->
            <div class="d-flex flex-column ga-2">
              <template v-if="!isCurrentlyInCart(product)">
                <v-btn
                  size="small"
                  variant="outlined"
                  prepend-icon="mdi-shopping"
                  :loading="session?.busy.value"
                  :disabled="!session?.canEdit.value"
                  @click="addDamageWaiver(product, false)"
                >
                  {{ getAction(product) }}
                </v-btn>

                <v-btn
                  size="small"
                  color="primary"
                  prepend-icon="mdi-lightning-bolt"
                  :loading="session?.busy.value"
                  :disabled="!session?.canEdit.value"
                  @click="addDamageWaiver(product, true)"
                >
                  Purchase Now
                </v-btn>
              </template>

              <v-btn
                v-else
                size="small"
                variant="outlined"
                color="error"
                prepend-icon="mdi-delete"
                :loading="session?.busy.value"
                :disabled="!session?.isOpen.value"
                @click="removeDamageWaiver(product)"
              >
                Remove from Cart
              </v-btn>
            </div>
          </div>
        </v-list-item>
      </v-list>
    </v-card-text>
  </v-card>
</template>
<script lang="ts" setup>
import type { DamageWaiverTypeMember } from "../../../cbi-generator/generated/dto/DamageWaiverTypeMember";
import publicClient from "@/api/client";
import { DamageWaiverApiClient } from "../../../cbi-generator/frontend/api/DamageWaiverApiClient";
import { DamageWaiverFrontendService } from "../../../cbi-generator/frontend/services/DamageWaiverFrontendService";
import { ProductListing } from "@/api/models/shop/ProductListing";
import { useCatalog } from "@/composables/shop/useCatalog";
import { buildMemberCatalogPurchase } from "@/composables/shop/memberCatalogPurchase";
import {
  memberOrderSessionKey,
  type MemberOrderSession,
} from "@/composables/shop/useMemberOrder";
import { useNotificationStore } from "@/stores/notification";
import { useUser } from "@/composables/useUser";
const userStore = useUser();
const props = defineProps<{
  person?: { personId: number };
  requirePerson?: boolean;
}>();
const router = useRouter();
const session = inject<MemberOrderSession | null>(memberOrderSessionKey, null);
const notification = useNotificationStore();
//const typeName = computed(() => props.damageWaiverType.optionDisplayName);
const damageWaiverTypes = ref<DamageWaiverTypeMember[]>([]);

function getPrice(option: ProductOption) {
  return option.productListing.price.toString();
}

function getName(option: ProductOption) {
  return (
    option.type?.optionDisplayName ||
    option.type?.optionName ||
    option.productListing.itemName
  );
}

function isCurrentlyInCart(option: ProductOption) {
  return !!lineFor(option);
}

function getAction(option: ProductOption) {
  if (!isCurrentlyInCart(option)) return "Add to Cart";
  return "Update Cart";
}

new DamageWaiverFrontendService(new DamageWaiverApiClient(publicClient))
  .purchaseOptionsPublic()
  .then((types) => {
    damageWaiverTypes.value = types;
  })
  .catch(() => {});

type ProductOption = {
  productListing: ProductListing;
  type: DamageWaiverTypeMember;
};

const damageWaiverProducts = ref<ProductListing[]>();

useCatalog().then((a) => {
  damageWaiverProducts.value = a.getDamageWaiverItems();
});

const productOptions = computed(() => {
  return damageWaiverTypes.value
    ?.map((a) => {
      const productListing = damageWaiverProducts.value?.find(
        (b) => a?.assignId == b.damageWaiverTypeId,
      );
      return {
        productListing: productListing,
        type: a,
      };
    })
    .filter((a) => !!a.productListing && !!a.type) as ProductOption[];
});

function lineFor(option: ProductOption) {
  const instanceId = option.productListing.catalogItemInstance.assignId;
  return session?.order.value?.lineItems?.find(
    (line) => line.itemInstanceIdTargetIds.assignId === instanceId,
  );
}

async function removeDamageWaiver(option: ProductOption) {
  const line = lineFor(option);
  if (!session || !line) return;
  try {
    await session.remove({ assignId: line.assignId });
  } catch (error) {
    notification.addSnackbar(
      error instanceof Error ? error.message : "Unable to remove the waiver.",
      "error",
    );
  }
}

async function addDamageWaiver(option: ProductOption, checkoutNow: boolean) {
  if (!session) return;
  try {
    if (!session.orderRef.value) await session.open();
    const existing = lineFor(option);
    if (existing) await session.remove({ assignId: existing.assignId });
    const recipientId =
      props.person?.personId ?? userStore.currentPersonRef.value?.personId;
    await session.add(
      buildMemberCatalogPurchase(option.productListing, {
        recipient:
          recipientId != null && recipientId > 0
            ? { personId: recipientId }
            : null,
      }),
    );
    if (checkoutNow) await router.push({ name: "portal-cart" });
  } catch (error) {
    notification.addSnackbar(
      error instanceof Error ? error.message : "Unable to add the waiver.",
      "error",
    );
  }
}
</script>
